import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileCheck2,
  MapPin,
  ShieldCheck,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  useIntentPreview,
  bindGeneratedIntentPreviewResolution,
  resolveIntentPreview,
  type IntentPreviewData,
} from '../hooks/use-intent-preview'
import {
  applyIntentPreviewSemanticPatchToStudioStore,
  rejectIntentPreviewProposalInStudioStore,
} from '../services/agent-workspace-semantic-patch'
import { summarizeCogoCollabRequirement } from '@/features/collaboration-kernel'
import { useStudioLocale } from '@/i18n'
import { useStudioStore } from '@/store/studio-store'
import {
  CogoJsonPreview,
  previewInteractionModeFor,
} from '@/features/chat/components/cogo-json-preview'
import {
  AUTONOMY_BEHAVIOR,
  type AutonomyLevel,
  type SeverityLevel,
} from '../types'
import {
  authenticatedStudioGeneratedArtifactPersistence,
  buildGeneratedArtifactApprovalDecision,
  commitGeneratedArtifact,
} from '@/features/generated-artifact'

/**
 * Determine how to handle an intent based on autonomy level and severity.
 * Returns 'auto' | 'toast' | 'confirm'.
 */
export function getIntentBehavior(
  autonomyLevel: AutonomyLevel,
  severity: SeverityLevel,
): 'auto' | 'toast' | 'confirm' {
  return AUTONOMY_BEHAVIOR[autonomyLevel][severity]
}

// ─── Severity badge styles ───────────────────────────────────

const SEVERITY_STYLES: Record<SeverityLevel, { bg: string; text: string }> = {
  info: { bg: 'bg-muted', text: 'text-muted-foreground' },
  warning: { bg: 'bg-chart-4/20', text: 'text-chart-4' },
  critical: {
    bg: 'bg-destructive/20',
    text: 'text-destructive',
  },
}

function uniqueEvidenceRefs(preview: IntentPreviewData): string[] {
  return [
    ...(preview.anchor?.evidence_refs ?? []),
    ...(preview.semantic_patch?.evidence_refs ?? []),
  ].filter((ref, index, refs) => refs.indexOf(ref) === index)
}

function humanizeTechnicalLabel(value: string): string {
  return value
    .split(/[\\/]/)
    .pop()!
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._/-]+/g, ' ')
    .replace(/\bpreview selection\b/gi, 'Target')
    .replace(/\bcogo json\b/gi, 'project model')
    .trim()
}

function sentenceLabel(value: string): string {
  const label = humanizeTechnicalLabel(value)
  if (!label) return 'Review change'
  const normalized = label.toLowerCase()
  if (normalized.includes('report table semantics'))
    return 'Adjust report table'
  if (normalized.includes('table cell insets'))
    return 'Adjust table cell spacing'
  if (normalized.includes('table header')) return 'Adjust table header'
  if (normalized.includes('anchored document semantics'))
    return 'Update selected app area'
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`
}

function userFacingValidationItem(item: string): string {
  return item
    .replace(/Cogo semantic patch/gi, 'Draft change')
    .replace(/Semantic patch/gi, 'Draft change')
    .replace(/Evidence/g, 'Check')
}

function proofLabel(label: string, index: number): string {
  return `${label} ${index + 1}`
}

function PreviewComparison({
  rows,
  labels,
  compact = false,
}: {
  rows: IntentPreviewData['diff_rows']
  labels: {
    before: string
    after: string
    unchanged: string
    removed: string
    /** The compact form's screen-reader heading. Localized like the rest. */
    reviewNeeded: string
  }
  compact?: boolean
}) {
  if (rows.length === 0) return null

  return (
    <div
      className={`rounded-md border border-border bg-background/70 ${
        compact ? 'px-3 py-2' : 'px-3 py-2.5'
      }`}
      data-testid="intent-preview-comparison"
    >
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.field} className="min-w-0">
            <div className="truncate text-[10px] font-medium text-muted-foreground">
              {humanizeTechnicalLabel(row.field)}
            </div>
            <div className="mt-1 grid grid-cols-[4rem_minmax(0,1fr)] items-start gap-2">
              <div className="text-[10px] font-semibold uppercase leading-4 text-muted-foreground">
                {labels.before}
              </div>
              <div className="min-w-0 truncate text-xs text-muted-foreground line-through">
                {row.before || labels.unchanged}
              </div>
              <div className="text-[10px] font-semibold uppercase leading-4 text-primary">
                {labels.after}
              </div>
              <div className="min-w-0 rounded-md border border-primary/20 bg-primary/10 px-2 py-1 text-xs font-semibold leading-4 text-primary">
                <span className="line-clamp-2">
                  {row.after || labels.removed}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {compact && <div className="sr-only">{labels.reviewNeeded}</div>}
    </div>
  )
}

function previewChipFromAnchor(preview: IntentPreviewData) {
  const anchor = preview.anchor
  if (!anchor) return null
  const parts = anchor.anchor_id.split('.')
  const isPreviewAnchor = parts[0] === 'studio' && parts[1] === 'preview'
  const blockId = isPreviewAnchor && parts[2] ? parts[2] : undefined
  const elementId =
    isPreviewAnchor && parts.length > 3 ? parts.slice(3).join('.') : undefined
  return {
    element_id: elementId ?? anchor.anchor_id,
    element_type: preview.semantic_patch?.target_kind ?? anchor.kind,
    block_id: blockId ?? anchor.anchor_id,
    label: anchor.label,
  }
}

export function IntentPreviewHost() {
  const preview = useIntentPreview()
  const { strings } = useStudioLocale()
  const currentProject = useStudioStore((s) => s.currentProject)
  const setChatRailChip = useStudioStore((s) => s.setChatRailChip)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [commitInFlight, setCommitInFlight] = useState(false)
  const [commitError, setCommitError] = useState<string | null>(null)
  const commitOperationRef = useRef<{
    token: symbol
    resolution: NonNullable<
      ReturnType<typeof bindGeneratedIntentPreviewResolution>
    >
  } | null>(null)

  useEffect(() => {
    setCommitError(null)
  }, [preview?.id])

  if (!preview || preview.resolved) return null
  const activePreview = preview

  const severityStyle = SEVERITY_STYLES[preview.severity]
  const semanticPatch = preview.semantic_patch
  const surfaceProposal = preview.surface_proposal
  const validationPlan = semanticPatch?.validation_plan ?? []
  const evidenceRefs = uniqueEvidenceRefs(preview)
  const collaborationSummary = preview.requirement
    ? summarizeCogoCollabRequirement(
        currentProject,
        preview.requirement.requirement_id,
      )
    : null
  const hasCollabConflict = Boolean(
    collaborationSummary?.transaction?.status === 'conflicted' ||
    collaborationSummary?.conflicts.length,
  )
  const previewComparisonRows = preview.diff_rows.slice(0, 2)
  const proposal = strings.proposal
  const severityLabel = {
    info: proposal.info,
    warning: proposal.warning,
    critical: proposal.critical,
  }[preview.severity]

  async function commitApprovedGeneratedSurface(): Promise<boolean> {
    if (
      !surfaceProposal ||
      !('origin' in surfaceProposal) ||
      surfaceProposal.origin !== 'studio-generation'
    ) {
      return true
    }
    const candidate = activePreview.generated_artifact_candidate
    if (!candidate || candidate.proposal !== surfaceProposal) {
      setCommitError(
        '이 미리보기는 인증된 프로젝트 revision과 연결되지 않아 저장할 수 없습니다. Chat에서 다시 생성해 주세요.',
      )
      return false
    }
    const targetProjectId = candidate.envelope.target.projectId
    if (useStudioStore.getState().currentProjectId !== targetProjectId) {
      setCommitError(
        '이 미리보기는 현재 열린 프로젝트와 다릅니다. 현재 프로젝트에서 다시 생성해 주세요.',
      )
      return false
    }
    const approvalActorId = useStudioStore.getState().session?.user?.id ?? null
    if (!approvalActorId) {
      setCommitError('승인 사용자를 확인할 수 없습니다. 다시 로그인해 주세요.')
      return false
    }
    if (approvalActorId !== candidate.requestedByUserId) {
      setCommitError(
        '생성 요청 사용자와 현재 로그인 사용자가 다릅니다. 다시 생성해 주세요.',
      )
      return false
    }
    if (commitOperationRef.current) return false

    const resolution = bindGeneratedIntentPreviewResolution(activePreview)
    if (!resolution) {
      setCommitError(
        '이 미리보기의 생성 authority를 확인할 수 없습니다. Chat에서 다시 생성해 주세요.',
      )
      return false
    }
    const operation = {
      token: Symbol(activePreview.id),
      resolution,
    }
    commitOperationRef.current = operation
    setCommitError(null)
    setCommitInFlight(true)
    const ownsOperation = () =>
      commitOperationRef.current?.token === operation.token
    const isOperationPreviewCurrent = () =>
      ownsOperation() && operation.resolution.isCurrent()
    const showOperationError = (message: string) => {
      if (isOperationPreviewCurrent()) {
        setCommitError(message)
      }
    }
    try {
      const result = await commitGeneratedArtifact({
        proposal: candidate.proposal,
        envelope: candidate.envelope,
        decision: buildGeneratedArtifactApprovalDecision({
          candidate,
          actorId: approvalActorId,
          decision: 'approved',
        }),
        persistence: authenticatedStudioGeneratedArtifactPersistence,
      })
      if (result.status !== 'committed') {
        showOperationError(result.error.message)
        return false
      }

      if (
        !isOperationPreviewCurrent() ||
        useStudioStore.getState().currentProjectId !== targetProjectId
      ) {
        showOperationError(
          '저장 중 열린 프로젝트가 변경되었습니다. 저장된 프로젝트는 프로젝트 목록에서 확인해 주세요.',
        )
        return false
      }

      await useStudioStore
        .getState()
        .openServerProject(result.receipt.projectId)
      const hydrated = useStudioStore.getState()
      if (
        !isOperationPreviewCurrent() ||
        hydrated.currentProjectId !== result.receipt.projectId ||
        !hydrated.currentProject ||
        hydrated.projectLoadError
      ) {
        showOperationError(
          '앱은 저장되었지만 프로젝트를 다시 열지 못했습니다. 프로젝트 목록에서 다시 열어 주세요.',
        )
        return false
      }
      return operation.resolution.resolve()
    } catch (error) {
      showOperationError(
        error instanceof Error
          ? error.message
          : '생성된 앱을 저장하지 못했습니다.',
      )
      return false
    } finally {
      if (ownsOperation()) {
        commitOperationRef.current = null
        setCommitInFlight(false)
      }
    }
  }

  async function handleApprove() {
    if (hasCollabConflict) return
    if (surfaceProposal) {
      const isGeneratedApplication =
        'origin' in surfaceProposal &&
        surfaceProposal.origin === 'studio-generation'
      if (!(await commitApprovedGeneratedSurface())) return
      if (!isGeneratedApplication) resolveIntentPreview(activePreview.id)
      return
    }
    applyIntentPreviewSemanticPatchToStudioStore(activePreview)
    resolveIntentPreview(activePreview.id)
  }

  function handleDismiss() {
    if (commitInFlight) return
    setCommitError(null)
    const nextChip = previewChipFromAnchor(activePreview)
    if (nextChip) setChatRailChip(nextChip)
    window.dispatchEvent(new CustomEvent('cogo:focus-chat-input'))
  }

  function handleKeepCurrentEdit() {
    if (commitInFlight) return
    if (surfaceProposal) {
      setCommitError(null)
      resolveIntentPreview(activePreview.id)
      return
    }
    rejectIntentPreviewProposalInStudioStore(activePreview)
    resolveIntentPreview(activePreview.id)
  }

  return (
    <div
      className="space-y-3 rounded-lg border bg-secondary p-4 animate-in fade-in slide-in-from-bottom-2 duration-300"
      data-testid="intent-preview-host"
    >
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="min-w-0 truncate text-base font-semibold leading-tight">
          {/* `sentenceLabel` is an ENGLISH humanizer — it title-cases a
              technical verb and rewrites a handful of English phrases. It is
              right for a raw `action_verb` and wrong for a surface, whose
              emitter sends prose ("preview generated application") that then
              rendered in English inside a Korean UI. A surface card has a
              localized heading available and its own `summary` underneath
              naming the specific application, so it uses those. */}
          {semanticPatch || surfaceProposal
            ? proposal.change
            : sentenceLabel(preview.action_verb)}
        </span>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-normal ${severityStyle.bg} ${severityStyle.text}`}
        >
          {severityLabel}
        </span>
      </div>

      {surfaceProposal && (
        <div
          className="space-y-2 rounded-md border border-primary/20 bg-background/70 p-2"
          data-testid="agent-surface-proposal-preview"
        >
          {/* ONE renderer, and ONE mapping — `previewInteractionModeFor`,
              shared with the other card that draws these proposals so the two
              cannot answer differently. The ternary that used to live here sent
              an INTERACTIVE generated application to `agent-surface-preview`,
              which re-admits through the closed six-type guest vocabulary and
              refuses the real Cogo vocabulary a generated block is written in.
              That was harmless only while the kind pinned `interactive: false`
              and the branch could not be reached. */}
          <CogoJsonPreview
            json={JSON.stringify(surfaceProposal.surface.cogoJson)}
            interactionMode={previewInteractionModeFor(surfaceProposal)}
          />
          <p className="px-1 text-xs text-muted-foreground">
            {surfaceProposal.summary}
          </p>
          {/* Interaction hints belong to the agent-surface kind: a canned
              scenario declares which events its program wired up. A generated
              application declares none — nothing wrote them, and inventing a
              row per event would be this card claiming an authorship it does
              not have. Absent, not empty. */}
          {'interactionHints' in surfaceProposal &&
            surfaceProposal.interactionHints.length > 0 && (
            <div className="flex flex-wrap gap-1 px-1">
              {surfaceProposal.interactionHints.map((hint) => (
                <span
                  key={`${hint.event}:${hint.description}`}
                  className="rounded-full border border-border bg-card px-2 py-1 text-[10px] text-muted-foreground"
                >
                  {hint.description}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="rounded-md border border-border bg-background/70 px-3 py-2">
        {semanticPatch && (
          <div className="truncate text-sm font-medium text-foreground">
            {sentenceLabel(semanticPatch.operation)}
          </div>
        )}
        {preview.anchor && (
          <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="truncate">{preview.anchor.label}</span>
          </div>
        )}
        {preview.requirement && (
          <p className="mt-2 line-clamp-2 text-sm text-foreground">
            {preview.requirement.body}
          </p>
        )}
        {collaborationSummary?.message && (
          <div
            className={`mt-2 flex items-start gap-1.5 rounded-md border px-2 py-1.5 text-[11px] leading-4 ${
              hasCollabConflict
                ? 'border-destructive/30 bg-destructive/10 text-destructive'
                : 'border-border bg-card text-muted-foreground'
            }`}
            data-testid="intent-preview-collab-status"
          >
            {hasCollabConflict ? (
              <AlertTriangle
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
                aria-hidden
              />
            ) : (
              <ShieldCheck
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
                aria-hidden
              />
            )}
            <span className="min-w-0">{collaborationSummary.message}</span>
          </div>
        )}
        {hasCollabConflict && (
          <div
            className="mt-2 rounded-md border border-destructive/20 bg-destructive/5 px-2 py-2 text-[11px] leading-4 text-destructive"
            data-testid="intent-preview-conflict-actions"
          >
            <div>{proposal.anotherEditPending}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                className="inline-flex min-h-8 items-center rounded-md border border-destructive/30 bg-background px-2 text-[11px] font-medium text-destructive transition hover:bg-destructive/10"
                onClick={() => setDetailsOpen(true)}
                aria-label={proposal.compareConflict}
              >
                {proposal.details}
              </button>
            </div>
          </div>
        )}
        <PreviewComparison
          rows={previewComparisonRows}
          labels={proposal}
          compact
        />
        <button
          type="button"
          className="mt-2 inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
          aria-expanded={detailsOpen}
          aria-controls="intent-preview-details"
          onClick={() => setDetailsOpen((open) => !open)}
        >
          {detailsOpen ? (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          )}
          {detailsOpen ? proposal.close : proposal.details}
        </button>
      </div>

      {detailsOpen && (
        <div id="intent-preview-details" className="space-y-3">
          {preview.anchor && (
            <div className="rounded-md border border-border bg-background/70 px-3 py-2">
              <div className="flex min-w-0 items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="truncate">
                  {humanizeTechnicalLabel(preview.anchor.kind)}
                </span>
              </div>
              <div className="mt-1 truncate text-xs text-foreground">
                {preview.anchor.label}
              </div>
              <div className="mt-1 flex min-w-0 flex-wrap gap-1">
                {preview.anchor.target_formats.map((format) => (
                  <span
                    key={format}
                    className="rounded border border-border bg-card px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
                  >
                    {format}
                  </span>
                ))}
              </div>
            </div>
          )}

          {preview.requirement?.acceptance_criteria.length ? (
            <div className="rounded-md border border-border bg-card px-3 py-2">
              <div className="text-[11px] font-medium text-muted-foreground">
                {proposal.whatWillCheck}
              </div>
              <div className="mt-2 space-y-1">
                {preview.requirement.acceptance_criteria
                  .slice(0, 3)
                  .map((criterion) => (
                    <div
                      key={criterion}
                      className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground"
                    >
                      <FileCheck2
                        className="h-3.5 w-3.5 shrink-0"
                        aria-hidden
                      />
                      <span className="truncate">{criterion}</span>
                    </div>
                  ))}
              </div>
            </div>
          ) : null}

          {semanticPatch && (
            <div className="rounded-md border border-border bg-background/70 px-3 py-2">
              <div className="flex min-w-0 items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-foreground">
                    {sentenceLabel(semanticPatch.operation)}
                  </div>
                  <div className="truncate font-mono text-[11px] text-muted-foreground">
                    {humanizeTechnicalLabel(semanticPatch.target_kind)}
                  </div>
                </div>
                <span className="shrink-0 rounded border border-border bg-card px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                  {semanticPatch.may_patch_cogo_json_directly
                    ? proposal.readyToUse
                    : proposal.reviewFirst}
                </span>
              </div>
            </div>
          )}

          <PreviewComparison rows={preview.diff_rows} labels={proposal} />

          {preview.impact_summary && (
            <p className="text-xs text-muted-foreground">
              {preview.impact_summary}
            </p>
          )}

          {(validationPlan.length > 0 || evidenceRefs.length > 0) && (
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="rounded-md border border-border bg-card px-3 py-2">
                <div className="mb-1 text-[11px] font-medium text-muted-foreground">
                  {proposal.reviewChecks}
                </div>
                {(validationPlan.length > 0
                  ? validationPlan
                  : [proposal.reviewBeforeApply]
                )
                  .slice(0, 3)
                  .map((item) => (
                    <div
                      key={item}
                      className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground"
                    >
                      <FileCheck2
                        className="h-3.5 w-3.5 shrink-0"
                        aria-hidden
                      />
                      <span className="truncate">
                        {userFacingValidationItem(item)}
                      </span>
                    </div>
                  ))}
              </div>
              <div className="rounded-md border border-border bg-card px-3 py-2">
                <div className="mb-1 text-[11px] font-medium text-muted-foreground">
                  {proposal.attachedProof}
                </div>
                {(evidenceRefs.length > 0
                  ? evidenceRefs
                  : [proposal.notAttached]
                )
                  .slice(0, 3)
                  .map((ref, index) => (
                    <div
                      key={ref}
                      className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground"
                      data-proof-ref={ref}
                    >
                      <ShieldCheck
                        className="h-3.5 w-3.5 shrink-0"
                        aria-hidden
                      />
                      <span className="truncate">
                        {ref === proposal.notAttached
                          ? proposal.notAttached
                          : proofLabel(proposal.check, index)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {commitError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
        >
          {commitError}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={() => { void handleApprove() }}
          disabled={hasCollabConflict || commitInFlight}
          className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-opacity ${
            hasCollabConflict || commitInFlight
              ? 'cursor-not-allowed opacity-50'
              : 'hover:opacity-90'
          }`}
          aria-label={hasCollabConflict ? proposal.reviewNeeded : proposal.use}
        >
          <CheckCircle2 className="h-4 w-4" aria-hidden />
          {hasCollabConflict ? proposal.reviewNeeded : proposal.use}
        </button>
        <button
          onClick={handleDismiss}
          disabled={commitInFlight}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
          aria-label={proposal.reviseAria}
        >
          {proposal.revise}
        </button>
        <button
          onClick={handleKeepCurrentEdit}
          disabled={commitInFlight}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label={proposal.keepCurrentAria}
        >
          <X className="h-4 w-4" aria-hidden />
          {proposal.keepCurrent}
        </button>
      </div>
    </div>
  )
}
